import { inject, Injectable, signal } from '@angular/core';
import { UtilsService, CoreService } from 'src/app/common';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { Project, ProjectMembership } from '../models/project.model';

export interface ProjectDeletionImpact {
    projectId: string;
    chatbots: number;
    digitalBusinessCards: number;
    contactSubmissions: number;
    contentHistoryItems: number;
    articleWorkflowItems: number;
    providerCredentials: number;
    hasActiveCredentialBindings: boolean;
    totalDependencies: number;
    canDelete: boolean;
    assetReassignmentEnabled: boolean;
    contactReassignmentEnabled?: boolean;
    contentReassignmentEnabled?: boolean;
    canReassignAssets: boolean;
}

export interface ProjectAssetReassignmentResult {
    operationId: string;
    sourceProjectId: string;
    targetProjectId: string;
    chatbotsMoved: number;
    digitalBusinessCardsMoved: number;
    contactSubmissionsMoved?: number;
    contentAssetsMoved?: number;
    remainingImpact: ProjectDeletionImpact;
}

@Injectable({
  providedIn: 'root'
})
export class OrganizationProjectsService {

    // 1. Signal to hold the state of projects
    private _projects = signal<Project[]>([]);
    public projects = this._projects.asReadonly();
    private _projectsOrgId = signal<number | string | null>(null);
    public projectsOrgId = this._projectsOrgId.asReadonly();

    private _projectMembers = signal<ProjectMembership[]>([]);
    public projectMembers = this._projectMembers.asReadonly();

    private _isProjectMembersLoading = signal<boolean>(false);
    public isProjectMembersLoading = this._isProjectMembersLoading.asReadonly();

    private core = inject(CoreService<{}>);
    private utils = inject(UtilsService);

    private identityBaseUrl = environment.identityUrl; // change to your projects API base if different

    /**
     * Loads all projects for an org, updating the signal.
     * @param orgId The organization ID.
     * @param force If true, will reload even if already populated.
     */
    async loadProjectsByOrgIdIfNeeded(orgId: number | string, force = false): Promise<void> {
        const currentProjectsOrgId = this._projectsOrgId();
        const hasProjectsForOrg = currentProjectsOrgId === orgId;
        if (hasProjectsForOrg && !force) {
            return;
        }

        const projects = await this.fetchProjectsByOrgId(orgId);
        if (projects) {
            this._projects.set(projects);
            this._projectsOrgId.set(orgId);
        } else {
            this._projects.set([]);
            this._projectsOrgId.set(null);
        }
    }

    public clearCache(): void {
        this._projects.set([]);
        this._projectsOrgId.set(null);
        this._projectMembers.set([]);
    }

    /**
     * Fetches projects for an organization by orgId.
     * GET /api/v1/Project/org/{orgId}
     * @returns Promise<Project[] | null>
     */
    async fetchProjectsByOrgId(orgId: number | string): Promise<Project[] | null> {
        this.utils.logger.info('fetchProjectsByOrgId() Fetching projects for org:', orgId);

        if (!orgId) {
            this.utils.logger.warn('fetchProjectsByOrgId() No orgId provided');
            return null;
        }

        try {
            const url = `${this.identityBaseUrl}/Project/org/${orgId}`;
            this.utils.logger.info('fetchProjectsByOrgId() API URL:', url);

            const data = await firstValueFrom(this.core.get(url));
            this.utils.logger.info('fetchProjectsByOrgId() API response data:', data);
            return Array.isArray(data) ? data as Project[] : [];

        } catch (error) {
            this.utils.logger.error('fetchProjectsByOrgId() API error:', error);
            return null;

        } finally {
            this.utils.logger.info('fetchProjectsByOrgId() Completed fetching projects for org:', orgId);
        }
    }

    /**
     * Loads project memberships for access management.
     * @param projectId Project UUID string
     * @param force If true, force refresh from API
     */
    async loadProjectMembers(projectId: string, force = false): Promise<void> {
        if (this._projectMembers().length > 0 && !force) return;
            const projectMembers = await this.fetchProjectMembers(projectId);
        if (projectMembers) {
            this.utils.logger.info('loadProjectMembers() Setting project members:', projectMembers);
            this._projectMembers.set(projectMembers);
        }
    }

    /**
     * Fetches project memberships (access list) from API.
     */
    async fetchProjectMembers(projectId: string): Promise<ProjectMembership[] | null> {
        this.utils.logger.info('fetchProjectMembers() for project:', projectId);

        if (!projectId) {
            this.utils.logger.warn('fetchProjectMembers() No projectId provided');
            return null;
        }
        this._isProjectMembersLoading.set(true);
        try {
            const url = `${this.identityBaseUrl}/ProjectMemberships/project/${projectId}`;
            this.utils.logger.info('fetchProjectMembers() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            return Array.isArray(data) ? data as ProjectMembership[] : [];

        } catch (error) {
            this.utils.logger.error('fetchProjectMembers() error:', error);
        return null;

        } finally {
            this.utils.logger.info('fetchProjectMembers() completed for project:', projectId);
            this._isProjectMembersLoading.set(false);
        }
    }

    /**
     * Fetches all project memberships for a user in the current organization.
     */
    public async fetchProjectMembershipsByUserId(userId: string): Promise<ProjectMembership[] | null> {
        this.utils.logger.info('fetchProjectMembershipsByUserId() for user:', userId);
        if (!userId) {
            this.utils.logger.warn('fetchProjectMembershipsByUserId() No userId provided');
            return null;
        }

        try {
            const encodedUserId = encodeURIComponent(userId);
            const url = `${this.identityBaseUrl}/ProjectMemberships/user/${encodedUserId}`;
            const data = await firstValueFrom(this.core.get(url));
            return Array.isArray(data) ? data as ProjectMembership[] : [];
        } catch (error) {
            this.utils.logger.error('fetchProjectMembershipsByUserId() error:', error);
            return null;
        }
    }

    /**
     * Adds a single user as a project member.
     * @param membership Minimal membership info: projectId, userId, role, orgId.
     * @returns The created ProjectMembership object, or null on failure.
     */
    async addProjectMembership(
        membership: {
        projectId: string;
        userId: string;
        role: string;
        orgId: number;
        }
    ): Promise<ProjectMembership | null> {
        this.utils.logger.info('addProjectMembership() Adding user to project', membership);

        try {
            const url = `${this.identityBaseUrl}/ProjectMemberships`;
            this.utils.logger.info('addProjectMembership() API URL:', url);
            const data = await firstValueFrom(this.core.post(url, membership));
            return data as ProjectMembership;
        } catch (error) {
            this.utils.logger.error('addProjectMembership() error:', error);
            return null;
        } finally {
            this.utils.logger.info('addProjectMembership() completed');
        }
    }

     /**
     * Deletes a project membership by its ID.
     * @param id The project membership ID to delete.
     * @returns Promise<boolean> True if successful, false otherwise.
     */
    async removeProjectMembership(id: string): Promise<boolean> {
        if (!id) {
            this.utils.logger.error('removeProjectMembership() No ID provided.');
            return false;
        }
        try {
            const url = `${this.identityBaseUrl}/ProjectMemberships/${id}`;
            this.utils.logger.info('removeProjectMembership() API URL:', url);
            await firstValueFrom(this.core.delete(url));
            this.utils.logger.info('removeProjectMembership() success');
            return true;
        } catch (error) {
            this.utils.logger.error('removeProjectMembership() error:', error);
            return false;
        }
    }

     /**
     * Updates a project membership.
     * @param membership ProjectMembership object
     * @returns Promise<ProjectMembership | null>
     */
    async updateProjectMembership(membership: ProjectMembership): Promise<ProjectMembership | null> {
        if (!membership?.id) {
            this.utils.logger.error('updateProjectMembership() No membership id provided.');
            return null;
        }
        try {
            const url = `${this.identityBaseUrl}/ProjectMemberships/${membership.id}`;
            this.utils.logger.info('updateProjectMembership() API URL:', url);
            const data = await firstValueFrom(this.core.bulkPut(url, membership));
            this.utils.logger.info('updateProjectMembership() success:', data);
            // No content returned, so we return what we sent as confirmation
            return membership;
        } catch (error) {
            this.utils.logger.error('updateProjectMembership() error:', error);
            return null;
        }
    }

    async deleteProject(projectId: string, userId: string): Promise<{ data: any; error: any }> {
        const response = { data: null, error: null };
        
        this.utils.logger.info('deleteProject() Deleting project:', projectId, 'for org:', userId);


        try {
            const url = `${this.identityBaseUrl}/Project/${projectId}/${userId}`;
            this.utils.logger.info('deleteProject() API URL:', url);
            
            response.data = await firstValueFrom(this.core.delete(url));
            this.utils.logger.info('deleteProject() Success:', response.data);

            // Update the projects signal to remove the deleted project
            this._projects.update(projects => projects.filter(p => p.id !== projectId));
            
            return response;
        } catch (error) {
            this.utils.logger.error('deleteProject() API error:', error);
            response.error = error;
            return response;
        }
    }

    public async getProjectDeletionImpact(
        projectId: string
    ): Promise<{ data: ProjectDeletionImpact | null; error: unknown }> {
        const response: { data: ProjectDeletionImpact | null; error: unknown } = { data: null, error: null };
        try {
            response.data = await firstValueFrom(
                this.core.get(`${this.identityBaseUrl}/project/${encodeURIComponent(projectId)}/deletion-impact`)
            ) as ProjectDeletionImpact;
        } catch (error) {
            response.error = error;
        }

        return response;
    }

    public async reassignProjectAssets(
        sourceProjectId: string,
        targetProjectId: string,
        operationId: string
    ): Promise<{ data: ProjectAssetReassignmentResult | null; error: unknown }> {
        const response: { data: ProjectAssetReassignmentResult | null; error: unknown } = { data: null, error: null };
        try {
            response.data = await firstValueFrom(this.core.post(
                `${this.identityBaseUrl}/project/${encodeURIComponent(sourceProjectId)}/reassign-assets`,
                { targetProjectId, operationId }
            )) as ProjectAssetReassignmentResult;
        } catch (error) {
            response.error = error;
        }

        return response;
    }
}
